import { message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import { useAuthStore } from '../store';
import { environments } from '../constants';
import { parseError } from '../utils/parseError';
import { useLoginMutationeArgs } from '../models';
import { LOGIN } from '../graphql/Mutations';
import { UserException, populateSessionStorage } from '../utils/helpers';
import {
  add_invite_member_to_organization,
  adminLogin,
  redeemInvite,
} from '../services/AuthActions';
import { useAddUserToGroup } from './useAddUserToGroup';
import {
  ESessionStorageKeys,
  IRedeemInviteInput,
  IRedeemInvitePayload,
  TAuthResultUserData,
} from '../../../types';

const useInviteMutation = ({
  variable,
  onCompletedCallback,
}: useLoginMutationeArgs) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const inviteDetail = useAuthStore((s) => s.inviteDetail);
  const signUpFormInputs = useAuthStore((s) => s.signUpInputs);
  const setAuthChallengeState = useAuthStore((s) => s.setAuthChallengeState);
  const setLoginInputs = useAuthStore((s) => s.setLoginInputs);

  const groupName = inviteDetail?.inviteResult?.invite?.user_type;

  const { handleAddUserToGroup, loading: addUserToGroupLoading } =
    useAddUserToGroup({
      onSuccessCallback: () => {},
      onErrorCallback: () => {
        throw UserException(
          'Failed to add user to Cognito Group [useInviteMutation]'
        );
      },
    });

  const [
    loginMutation,
    { loading: loginLoading, data: loginData, error: loginError },
  ] = useMutation(LOGIN);

  const handleInviteLogin = useCallback(() => {
    loginMutation({
      variables: {
        username: variable?.email
          ? variable.email.toLowerCase()
          : variable.username,
        password: variable.password,
        clientId: environments.REACT_APP_DEPLOY_USER_POOL_WEB_CLIENT_ID,
      },
    });
  }, [variable]);

  useEffect(() => {
    if (!loginData || loginError) return;

    if (loginData?.login?.Error) {
      setLoading(false);
      setError(loginData.login.Error);
      message.error(
        typeof loginData.login.Error.message === 'string'
          ? loginData.login.Error.message
          : 'An error occurred during login.'
      );
      return;
    }

    const processLogin = async () => {
      try {
        setLoading(true);

        if (loginData?.login) {
          if (loginData?.login?.MFAEnabled) {
            setAuthChallengeState(loginData.login);
            setLoginInputs(variable);

            message.success(
              'We have resent your OTP code to your preferred communication method. Please check and submit the code for verification to proceed.'
            );

            setLoading(false);
            onCompletedCallback();
          } else {
            if (inviteDetail) {
              if (!signUpFormInputs?.username || !groupName) {
                throw UserException(
                  'useInviteMutation - Missing required username or groupName'
                );
              }

              const user_group_add = await handleAddUserToGroup({
                username: signUpFormInputs.username,
                groupName: groupName,
              });

              if (!user_group_add?.adminAddUserToGroup?.text) {
                throw UserException(
                  'handleAddUserToGroup - Failed to add user to Cognito Group [useInviteMutation]'
                );
              }

              const redeemInviteResult = await redeemInvite(
                inviteDetail.inviteId,
                inviteDetail.email.toLowerCase()
              );

              if (!redeemInviteResult) {
                throw UserException('redeemInvite - Error redeeming invite');
              }

              const email = signUpFormInputs?.email.toLowerCase();
              const password = signUpFormInputs?.password;
              const username = signUpFormInputs?.username;
              const lastName = signUpFormInputs?.lastName;
              const clientId =
                environments.REACT_APP_DEPLOY_USER_POOL_WEB_CLIENT_ID;

              if (!lastName || !username || !password || !email || !clientId) {
                throw new Error(
                  'useInviteMutation - Required redeem-invite values are missing'
                );
              }

              const payload: IRedeemInvitePayload = {
                org_role: 'Admin',
                member_status: 'Active',
                statutory_details: {
                  gender: signUpFormInputs.gender,
                  first_name: signUpFormInputs.firstName,
                  last_name: lastName,
                },
                contact_details: {
                  personal: {
                    email_address: email,
                    mobile_number: signUpFormInputs.phoneNumber,
                  },
                },
              };

              const formObject: IRedeemInviteInput = {
                payload: payload,
                jwt: loginData?.login?.AuthenticationResult?.AccessToken,
                org_id: inviteDetail.inviteResult.org_id,
                username: username,
              };

              const inviteMemberResult =
                await add_invite_member_to_organization(formObject);

              if (!inviteMemberResult) {
                throw UserException(
                  'add_invite_member_to_organization - Error adding invite member to organization'
                );
              }

              const adminLoginResult = await adminLogin({
                username: email,
                password,
                clientId,
              });

              if (adminLoginResult === null) {
                throw UserException('adminLogin - Failed to login as admin');
              }

              populateSessionStorage([
                {
                  key: ESessionStorageKeys.AUTH_TOKEN,
                  value: adminLoginResult.AccessToken,
                },
                {
                  key: ESessionStorageKeys.REFRESH_TOKEN,
                  value: adminLoginResult.RefreshToken,
                },
                {
                  key: ESessionStorageKeys.ID_TOKEN,
                  value: adminLoginResult.IdToken,
                },
                {
                  key: ESessionStorageKeys.ORG_ID,
                  value: inviteDetail.inviteResult.org_id,
                },
              ]);

              const userDataPayload: TAuthResultUserData = {
                token: adminLoginResult.AccessToken,
                refresh: adminLoginResult.RefreshToken,
                sub: username,
                org_id: inviteDetail.inviteResult.org_id,
              };

              const userData = JSON.stringify(userDataPayload);

              setLoading(false);
              navigate(`/login-redirect/sso-app/${userData}`);
            }
          }
        } else {
          message.error(
            'Something went wrong... Please contact our support services'
          );
          setLoading(false);
          navigate('/login/sso/app');
        }
      } catch (err: any) {
        console.error('useInviteMutation [Success]:\n', err);
        setError(err);
        setLoading(false);
      }
    };

    processLogin();
  }, [variable, loginData, inviteDetail, signUpFormInputs]);

  useEffect(() => {
    if (!loginError) return;

    const processLoginError = () => {
      try {
        setError(loginError);
        setLoading(false);

        const errorMessage = parseError(loginError);

        if (
          loginError?.message?.includes?.('PasswordResetRequiredException')
        ) {
          message.info('Password reset required for the user', 10);

          navigate('/forgot-password', {
            state: {
              isResetPassword: true,
            },
          });
        } else {
          message.error(errorMessage);
        }
      } catch (err: any) {
        console.error('useInviteMutation [Failure]', err);
        setError(err);
        setLoading(false);
      }
    };

    processLoginError();
  }, [variable, loginError]);

  return {
    loading:
      loginLoading === false &&
      loading === false &&
      addUserToGroupLoading === false
        ? false
        : true,
    error: error,
    handleInviteLogin,
  };
};

export default useInviteMutation;
